import { useCallback, useRef, useState } from 'react';
import { Requestor } from '~/src/api/Requestor';
import BulkDesignPage from './bulk-design';
import type { BulkDesignRefProps } from './bulk-design';

import { literal, type, Type, union, unknown, string } from 'io-ts';
import { useMessage } from '~/src/hooks/useMessage';
import { Quote } from '~/src/pricing/quote';
import { NamedTree } from '~/src/model/NamedTree';
import { TokenSecret, isTokenSecret } from '~/src/model/Authentication';
import { CreateRequestor as GlassdRequestor } from '~/src/api/Requestor/Glassd';
import { Config } from '~/src/model/Config';

interface EmbedBulkDesignPageProps {
	config?: Config;
}

export interface SettingsMessage {
	Tier: string;
	Install: {
		Delivery: {Kind: string };
		Labor: {Provider: string };
		Measure: {Provider: string };
		Install: {Provider: string };
	},
	Rules: {
		Org: string;
	}
}

export const SettingsMessage: Type<SettingsMessage> = type({
	Tier: string,
	Install: type({
		Delivery: type({
			Kind: string,
		}),
		Labor: type({
			Provider: string,
		}),
		Measure: type({
			Provider: string,
		}),
		Install: type({
			Provider: string,
		})
	}),
	Rules: type({
		Org: string,
	})
});

interface BulkDesignMessage {
	type: 'QUOTE' | 'BULK_DESIGN' | 'INIT_AUTH' | 'SETTINGS';
	data: Quote | NamedTree<unknown> | TokenSecret | SettingsMessage;
}

const BulkDesignMessage: Type<BulkDesignMessage> = type({
	type: union([literal('QUOTE'), literal('BULK_DESIGN'), literal('INIT_AUTH'), literal('SETTINGS')]),
	data: union([Quote, NamedTree(unknown), TokenSecret, SettingsMessage]),
});

const EmbedBulkDesignPage = ({ config }: EmbedBulkDesignPageProps) => {
	const [req, setReq] = useState<Requestor | undefined>(undefined);
	const bulkDesignRef = useRef<BulkDesignRefProps>(null);

	const handleMessage = useCallback(
		({ data }: MessageEvent<BulkDesignMessage>) => {
			console.log('Bulk Design message ', data);
			if (data.type === 'INIT_AUTH') {
				const auth = data.data as TokenSecret;
				if (isTokenSecret(auth) && config) {
					setReq(GlassdRequestor(config.SilicaEndpoint, auth));
				}
			} else if (data.type === 'QUOTE') {
				if (bulkDesignRef.current) {
					bulkDesignRef.current.updateQuote(data.data as Quote);
				}
			} else if (data.type === 'BULK_DESIGN') {
				// Do something here.
				if (bulkDesignRef.current) {
					bulkDesignRef.current.updateTreeData(data.data as NamedTree<unknown>);
				}
			} else if (data.type === 'SETTINGS') {
				// Do something here.
				if (bulkDesignRef.current) {
					bulkDesignRef.current.updateSettings(data.data as SettingsMessage);
				}
			}
		},
		[config]
	);

	useMessage(BulkDesignMessage, handleMessage);

	const onQuoteChange = useCallback((quote: Quote) => {
		const payload = {
			type: 'Save-Quote',
			data: quote,
		};
		if (window.top) {
			window.top.postMessage(payload, '*');
		} else if (window.parent) {
			window.parent.postMessage(payload, '*');
		}
	}, []);

	const onTreeDataChange = useCallback((tree: NamedTree<unknown>) => {
		const payload = {
			type: 'Save-Bulk-Design',
			data: tree,
		};
		if (window.top) {
			window.top.postMessage(payload, '*');
		} else if (window.parent) {
			window.parent.postMessage(payload, '*');
		}
	}, []);

	const onSaveChange = useCallback((tree: NamedTree<unknown>, quote: Quote) => {
		const payload = {
			type: 'Save-CE',
			data: {
				tree,
				quote,
			},
		};
		if (window.top) {
			window.top.postMessage(payload, '*');
		} else if (window.parent) {
			window.parent.postMessage(payload, '*');
		}
	}, []);

	const onCancel = useCallback(() => {
		// Do something here.
		const payload = {
			type: 'No-Save-CE',
		};
		if (window.top) {
			window.top.postMessage(payload, '*');
		} else if (window.parent) {
			window.parent.postMessage(payload, '*');
		}
	}, []);

	if (req) {
		return (
			<div className="embed-bulk-design">
				<BulkDesignPage
					ref={bulkDesignRef}
					req={req}
					onQuoteChange={onQuoteChange}
					onTreeDataChange={onTreeDataChange}
					onSaveChange={onSaveChange}
					onCancel={onCancel}
				/>
			</div>
		);
	}

	return <div> Loading… </div>;
};

export default EmbedBulkDesignPage;
